import { useEffect } from 'react';
import { map, startWith, takeUntil, withLatestFrom } from 'rxjs';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { WidgetPayload } from '@client/widgets/widget.types';
import { isValidState } from '../chart/utils/chart.util';
import { ChartState, useChartState } from '@client/state/chart-state.hook';
import { useWidget } from '../widget.hook';
import { useDraftState } from '@client/state/draft-form-state.hook';
import { useWindow } from '@client/window/window.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { trpc } from '@client-trpc';
import { resolveLayoutStateToFormState } from '@client/features/trading-layouts/layout-template-state.utils';

export const useTradingForm = () => {
  const { openWidget$ } = useWidget();
  const { openWindow, closeWindow } = useWindow();
  const { updateState, clearState } = useDraftState();
  const { getState$, dispatch } = useChartState();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();
  const trpcClient = trpc.useUtils().client;

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'TradingForm'>('TradingForm'),
        withLatestFrom(
          getState$().pipe(startWith(new Map<string, ChartState>()))
        ),
        map(
          ([{ id, formState: widgetFormState, ...rest }, stateMap]) => ({
            id,
            ...rest,
            state:
              widgetFormState ?? (id ? stateMap.get(id)?.formState : undefined)
          })
        ),
        takeUntil(workspaceDestroy$)
      )
      .subscribe({
        next: async ({
          type,
          id,
          state,
          mode,
          linkedTradingLayoutId,
          linkedTradingLayoutName,
          initSource,
          defaultTemplatePolicy
        }) => {
          let resolvedState = state;
          let resolvedLinkedLayoutId = linkedTradingLayoutId;
          let resolvedLinkedLayoutName = linkedTradingLayoutName;

          let effectiveInitSource =
            initSource ?? (state ? 'LAYOUT_RESTORE' : 'NEW_FORM');
          let effectiveDefaultTemplatePolicy =
            defaultTemplatePolicy ??
            (effectiveInitSource === 'NEW_FORM'
              ? 'AUTO_APPLY_DEFAULT_TEMPLATES'
              : 'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES');

          if (!resolvedState && effectiveInitSource === 'NEW_FORM') {
            try {
              const defaultLayout =
                await trpcClient.tradingLayouts.defaultLayout.query({ mode });

              if (defaultLayout) {
                resolvedState = await resolveLayoutStateToFormState(
                  trpcClient,
                  defaultLayout.snapshot.formState
                );
                resolvedLinkedLayoutId = defaultLayout.id;
                resolvedLinkedLayoutName = defaultLayout.name;
                effectiveInitSource = 'LAYOUT_RESTORE';
                effectiveDefaultTemplatePolicy =
                  'SKIP_AUTO_APPLY_DEFAULT_TEMPLATES';
              }
            } catch (error) {
              console.warn('Failed to load default layout:', error);
            }
          }

          clearState();

          openWindow({
            type: 'TradingForm',
            sizePreset: 'largeForm',
            componentProps: {
              chartId: id,
              state: resolvedState,
              mode,
              linkedTradingLayoutId: resolvedLinkedLayoutId,
              linkedTradingLayoutName: resolvedLinkedLayoutName,
              initSource: effectiveInitSource,
              defaultTemplatePolicy: effectiveDefaultTemplatePolicy,
              onSubmit: (formState) => {
                if (!isValidState(formState)) return;

                if (!id) {
                  dispatch({
                    type: 'ADD_CHART',
                    payload: { formState }
                  });
                } else {
                  dispatch({
                    type: 'UPDATE_CHART',
                    payload: { formState, id }
                  });
                }

                clearState();
                closeWindow(type);
              },
              onFormChange: updateState,
              onFormDestroy: clearState
            }
          });
        }
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    clearState,
    closeWindow,
    dispatch,
    getState$,
    openWidget$,
    openWindow,
    trpcClient,
    updateState,
    workspaceDestroy$
  ]);
};
